/* Riotball arena — dimensions, squad layout, sprite atlas map, team kits, match factory */
export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ---------- arena constants ----------
export const W = 960;
export const H = 420;
export const MATCH_FRAMES = 3600; // 60 seconds
export const PLAYER_X = 170;
export const GOAL_X = 876;
export const DEFENDER_X = 782;
export const MIN_Y = 92;
export const MAX_Y = 336;
export const GOAL_SPEED = 0.042; // e-goal side-to-side pace (2x league old speed)
export const WALL_TOP = MIN_Y - 34;
export const WALL_BOT = MAX_Y + 50;

/* VS CPU tiers — easy is the rookie league. Normal runs a faster carrier with a
   tighter steal window; Brutal telegraphs almost nothing and banks shots off the walls */
export const CPU_TIERS = {
  easy: { label: "EASY", carrierSpeed: 1.3, shotSpeed: 6.6, missChance: 0.12, goalDrift: 0.45, stealRange: 52, stealZoneX: 700, windupFrames: 34, bankChance: 0, winBonus: 0, combos: 1, comboSpeed: 11 },
  normal: { label: "NORMAL", carrierSpeed: 1.9, shotSpeed: 7.4, missChance: 0.08, goalDrift: 0.7, stealRange: 46, stealZoneX: 665, windupFrames: 26, bankChance: 0.3, winBonus: 5, combos: 2, comboSpeed: 13 },
  brutal: { label: "BRUTAL", carrierSpeed: 2.45, shotSpeed: 8.8, missChance: 0.05, goalDrift: 1.0, stealRange: 36, stealZoneX: 640, windupFrames: 18, bankChance: 0.55, winBonus: 15, combos: 3, comboSpeed: 15 },
};
// squad formation: 2 midfield + 2 front — the runner is the fifth player
export const MATES = [
  { x: 408, y: 152 },
  { x: 408, y: 292 },
  { x: 648, y: 138 },
  { x: 648, y: 300 },
];
// rival markers shade each squadmate from the goal side (past the first touch line) — full 5 v 5 with the keeper
export const MARKERS = MATES.map((m, i) => ({ x: Math.min(m.x + 120, 744), yOff: i % 2 ? 26 : -26, mark: i }));

// the league referee — paces the NEAR touchline in the foreground (below every
// play lane, never between the runner and the goal) and signals the calls
export const REF_GROUND_Y = 406;
export const REF_MIN_X = 300;
export const REF_MAX_X = 660;
export const REF_HEIGHT = 84;

// sprite cells measured from the official RIOTBALL sprite sheet (1116x2000)
export const SPRITES = {
  red: {
    idle: [67, 200, 59, 121],
    run1: [151, 369, 62, 115],
    run2: [329, 369, 70, 115],
    throw: [256, 539, 99, 120],
    celebrate: [159, 975, 62, 131],
    catch: [313, 717, 58, 111],
    fall: [152, 882, 87, 70],
  },
  white: {
    idle: [637, 200, 60, 120],
    run1: [720, 369, 51, 115],
    run2: [906, 369, 47, 115],
    throw: [780, 545, 87, 114],
    celebrate: [797, 975, 67, 131],
    catch: [810, 717, 66, 111],
    fall: [772, 882, 88, 70],
  },
};

export const TEAMS = {
  red: { name: "RED FORCE", short: "RED", color: "#ff0033", emblem: "/assets/team-logo-red-force.png", highKey: "riotball-high-red",
    trait: { name: "THE RESILIENCE", desc: "Zero retreat — turnovers reload almost twice as fast." } },
  white: { name: "WHITE GUARDS", short: "WHITE", color: "#e8e6e3", emblem: "/assets/team-logo-white-guards.png", highKey: "riotball-high-white",
    trait: { name: "PROTOCOL", desc: "Regulation efficiency — the match clock starts with +6 seconds." } },
  vultures: { name: "VOLTAIC VULTURES", short: "VULT", color: "#ffd84d", emblem: "/assets/team-logo-voltaic-vultures.png", highKey: "riotball-high-vultures",
    trait: { name: "STORM CURRENT", desc: "The Core leaves the hand 18% hotter." } },
  silverbacks: { name: "SILVERBACKS", short: "BACKS", color: "#c0c8d8", emblem: "/assets/team-logo-silverbacks.png", highKey: "riotball-high-silverbacks",
    trait: { name: "CONCRETE MASS", desc: "Slower feet — but the Core bull-rushes through the first marker hit of every throw." } },
  "iron-hearts": { name: "IRON HEARTS", short: "HEARTS", color: "#ff7d9c", emblem: "/assets/team-logo-iron-hearts.png", highKey: "riotball-high-iron-hearts",
    trait: { name: "THE BROKEN VALENTINE", desc: "Once a match, a drive-killing hit is shrugged off — cracked, not broken." } },
  "chrome-phantoms": { name: "CHROME PHANTOMS", short: "GHOST", color: "#7fd8ff", emblem: "/assets/team-logo-chrome-phantoms.png", highKey: "riotball-high-chrome-phantoms",
    trait: { name: "GHOST PROTOCOL", desc: "Once a match, the defender's block phases straight through the decoy." } },
  "obsidian-legion": { name: "OBSIDIAN LEGION", short: "COIL", color: "#8f6fd8", emblem: "/assets/team-logo-obsidian-legion.png", highKey: "riotball-high-obsidian-legion",
    trait: { name: "THE COIL", desc: "Bank-shot specialists — the Core comes off the live walls faster than it went in." } },
  "night-ravens": { name: "NIGHT RAVENS", short: "RAVEN", color: "#9a6bff", emblem: "/assets/team-logo-night-ravens.png", highKey: "riotball-high-night-ravens",
    trait: { name: "NIGHT SHIFT", desc: "The arena lights cut every few seconds — in the dark, the Ravens fly 60% faster." } },
  "aurora-syndicate": { name: "AURORA SYNDICATE", short: "SYNC", color: "#46e0d4", emblem: "/assets/team-logo-aurora-syndicate.png", highKey: "riotball-high-aurora-syndicate",
    trait: { name: "STATIC LINK", desc: "One shared mind — the zip-pass touch window is far wider." } },
  "verdigris-vanguard": { name: "VERDIGRIS VANGUARD", short: "BLOOM", color: "#4fd8a8", emblem: "/assets/team-logo-verdigris-vanguard.png", highKey: "riotball-high-verdigris-vanguard",
    trait: { name: "THE MACHINE BLOOM", desc: "Slow start, fast finish — legs ramp from 80% to 140% as the clock burns." } },
};

export const TEAM_ORDER = ["red", "white", "vultures", "silverbacks", "iron-hearts", "chrome-phantoms", "obsidian-legion", "night-ravens", "aurora-syndicate", "verdigris-vanguard"];

/* game keys → franchise dossier ids (backend FRANCHISE_IDS) — the first three
   predate the id scheme; everything else maps to itself */
export const TEAM_TO_FRANCHISE = { red: "red-force", white: "white-guards", vultures: "voltaic-vultures" };
export const FRANCHISE_TO_TEAM = { "red-force": "red", "white-guards": "white", "voltaic-vultures": "vultures" };
export const teamToFranchise = (key) => TEAM_TO_FRANCHISE[key] || key;
export const franchiseToTeam = (id) => FRANCHISE_TO_TEAM[id] || id;

/* home arena backdrops — one painted arena per franchise (fence + apocalyptic
   crowd), drawn behind the glass court by the match renderer */
export const arenaSrc = (teamKey) => `/assets/arenas/arena-${teamToFranchise(teamKey)}.jpg`;

/* owner-supplied neon court floors — one unique top-down floor per franchise;
   painted INSIDE the court lines on match day */
export const FLOOR_BY_TEAM = {
  red: "rust",
  "iron-hearts": "crimson",
  white: "chrome",
  silverbacks: "steel",
  "verdigris-vanguard": "verdant",
  "aurora-syndicate": "amber",
  vultures: "volt",
  "obsidian-legion": "ember",
  "chrome-phantoms": "azure",
  "night-ravens": "neon",
};
export const floorSrc = (teamKey) => `/assets/arenas/floor-${FLOOR_BY_TEAM[teamKey] || "rust"}.jpg`;

/* per-floor neon glow [r,g,b] — the goal ring and zip-pass streaks sync to it */
export const FLOOR_GLOW = {
  rust: [255, 62, 48],
  chrome: [225, 240, 255],
  steel: [110, 220, 200],
  verdant: [79, 216, 168],
  amber: [255, 170, 51],
  volt: [255, 216, 77],
  ember: [255, 96, 40],
  azure: [77, 184, 255],
  neon: [255, 77, 216],
  crimson: [255, 34, 68],
};
export const floorGlow = (teamKey) => FLOOR_GLOW[FLOOR_BY_TEAM[teamKey]] || FLOOR_GLOW.rust;

// red/white blit from the classic atlas above; the expansion teams load as
// individual transparent PNGs sliced from their official sprite sheets
export const IMG_SPRITE_TEAMS = {
  vultures: {
    idle: "/assets/teams/vultures/idle.png",
    run1: "/assets/teams/vultures/run1.png",
    run2: "/assets/teams/vultures/run2.png",
    throw: "/assets/teams/vultures/throw.png",
    celebrate: "/assets/teams/vultures/celebrate.png",
    catch: "/assets/teams/vultures/catch.png",
    fall: "/assets/teams/vultures/fall.png",
  },
  silverbacks: {
    idle: "/assets/teams/silverbacks/idle.png",
    run1: "/assets/teams/silverbacks/run1.png",
    run2: "/assets/teams/silverbacks/run2.png",
    throw: "/assets/teams/silverbacks/throw.png",
    celebrate: "/assets/teams/silverbacks/celebrate.png",
    catch: "/assets/teams/silverbacks/catch.png",
    fall: "/assets/teams/silverbacks/fall.png",
  },
  "iron-hearts": {
    idle: "/assets/teams/iron-hearts/idle.png",
    run1: "/assets/teams/iron-hearts/run1.png",
    run2: "/assets/teams/iron-hearts/run2.png",
    throw: "/assets/teams/iron-hearts/throw.png",
    celebrate: "/assets/teams/iron-hearts/celebrate.png",
    catch: "/assets/teams/iron-hearts/catch.png",
    fall: "/assets/teams/iron-hearts/fall.png",
  },
  "chrome-phantoms": {
    idle: "/assets/teams/chrome-phantoms/idle.png",
    run1: "/assets/teams/chrome-phantoms/run1.png",
    run2: "/assets/teams/chrome-phantoms/run2.png",
    throw: "/assets/teams/chrome-phantoms/throw.png",
    celebrate: "/assets/teams/chrome-phantoms/celebrate.png",
    catch: "/assets/teams/chrome-phantoms/catch.png",
    fall: "/assets/teams/chrome-phantoms/fall.png",
  },
  "obsidian-legion": {
    idle: "/assets/teams/obsidian-legion/idle.png",
    run1: "/assets/teams/obsidian-legion/run1.png",
    run2: "/assets/teams/obsidian-legion/run2.png",
    throw: "/assets/teams/obsidian-legion/throw.png",
    celebrate: "/assets/teams/obsidian-legion/celebrate.png",
    catch: "/assets/teams/obsidian-legion/catch.png",
    fall: "/assets/teams/obsidian-legion/fall.png",
  },
  "night-ravens": {
    idle: "/assets/teams/night-ravens/idle.png",
    run1: "/assets/teams/night-ravens/run1.png",
    run2: "/assets/teams/night-ravens/run2.png",
    throw: "/assets/teams/night-ravens/throw.png",
    celebrate: "/assets/teams/night-ravens/celebrate.png",
    catch: "/assets/teams/night-ravens/catch.png",
    fall: "/assets/teams/night-ravens/fall.png",
  },
  "aurora-syndicate": {
    idle: "/assets/teams/aurora-syndicate/idle.png",
    run1: "/assets/teams/aurora-syndicate/run1.png",
    run2: "/assets/teams/aurora-syndicate/run2.png",
    throw: "/assets/teams/aurora-syndicate/throw.png",
    celebrate: "/assets/teams/aurora-syndicate/celebrate.png",
    catch: "/assets/teams/aurora-syndicate/catch.png",
    fall: "/assets/teams/aurora-syndicate/fall.png",
  },
  "verdigris-vanguard": {
    idle: "/assets/teams/verdigris-vanguard/idle.png",
    run1: "/assets/teams/verdigris-vanguard/run1.png",
    run2: "/assets/teams/verdigris-vanguard/run2.png",
    throw: "/assets/teams/verdigris-vanguard/throw.png",
    celebrate: "/assets/teams/verdigris-vanguard/celebrate.png",
    catch: "/assets/teams/verdigris-vanguard/catch.png",
    fall: "/assets/teams/verdigris-vanguard/fall.png",
  },
};

export const timeAgo = (iso) => {
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "JUST NOW";
  if (s < 3600) return `${Math.floor(s / 60)}M AGO`;
  if (s < 86400) return `${Math.floor(s / 3600)}H AGO`;
  return `${Math.floor(s / 86400)}D AGO`;
};

export const freshMatch = (mode = "solo", tier = "easy") => ({
  mode, // "solo" = classic attack drill; "vs" = the CPU takes real drives at your goal
  tier, // "easy" | "normal" | "brutal" — vs CPU difficulty
  possession: "player", // "player" | "cpu" — vs mode alternates
  steals: 0, // lane-read pick-offs — +2 PTS each at the whistle
  intercepts: 0, // mid-combo lane cuts — +3 PTS each at the whistle
  cpuScore: 0,
  cpu: null, // { phase: "drive"|"windup"|"ball", timer, x, y }
  cpuBall: null, // { x, y, vx, vy } — the CPU's shot at your goal
  cpuGoalPhase: Math.random() * Math.PI * 2,
  cpuGoalY: 210,
  cpuNetRipple: 0, // net-burst frames when the CPU goal is seen going in
  holdTimer: 0, // vs shot clock — hold the ball ~8s and the defense strips it
  pendingCpu: false,
  frame: 0,
  playerY: 214,
  playerMoving: false,
  throwTimer: 0,
  celebrateTimer: 0,
  goalSparks: null, // neon burst at the scoring end — rendered in the floor's glow
  cpuGoalSparks: null, // rival-colored burst when the CPU scores
  crowdSurge: 0, // crowd wave — stands surge + pennants wave after a goal
  defenderFallTimer: 0,
  defenderCatchTimer: 0,
  defenderY: 214,
  goalPhase: Math.random() * Math.PI * 2,
  orb: { state: "held", x: 0, y: 0, vx: 0, vy: 0, touched: [], bounces: 0 },
  markY: [214, 214, 214, 214],
  timeout: false,
  tubeBoost: null,
  score: 0,
  timeLeft: MATCH_FRAMES,
  replayFlash: 0,
  banner: "",
  bannerTimer: 0,
  ref: { x: 480, dir: 1, state: "idle", timer: 70 },
  // one-shot franchise trait charges + live trait state
  traits: { heartUsed: false, ghostUsed: false, massUsedThrow: false, massRushed: -1, clockApplied: false },
  lightsOut: false,
});
